import { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Stack,
} from '@mui/material'
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material'
import { SPACING } from '@common/theme/spacing'
import { Dropdown, Option } from '@common/components/Form/Dropdown/Dropdown'
import { useForm } from 'react-hook-form'
import { TextField } from '@common/components/Form/TextField'
import RadioGroup from '@common/components/Form/RadioGroup'
import { zodResolver } from '@hookform/resolvers/zod'
import { capitalize } from '@common/utils/stringUtils'
import { formatQueryStatus } from './types'
import { ReopenQueryFormData, ReopenQueryValidationSchema } from '../schemas/queryValidationSchema'

export type ContainerSubmission = {
  id: string
  name: string
}

export type QueryInfo = {
  id: string
  pseudo_id: string
  status: string
}

export type TimepointSubmissionInfo = {
  name: string
  status: string
}

export interface ReopenQueryDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: ReopenQueryFormData) => void
  query: QueryInfo
  timepoint: TimepointSubmissionInfo
  containerSubmissions: ContainerSubmission[]
}

export default function ReopenQueryDialog({
  open,
  onClose,
  onConfirm,
  query,
  timepoint,
  containerSubmissions,
}: ReopenQueryDialogProps) {
  const { handleSubmit, control, reset, watch } = useForm<ReopenQueryFormData>({
    defaultValues: {
      container_ids: [],
      reason: '',
      requires_timepoint_resubmission: false,
    },
    resolver: zodResolver(ReopenQueryValidationSchema),
  })

  const reasonValue = watch('reason')
  const selectedContainers = watch('container_ids')
  const requiresTimepointResubmission = watch('requires_timepoint_resubmission')

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  const containerOptions: Option[] = containerSubmissions.map((container) => ({
    label: container.name,
    value: container.id,
  }))

  const resubmissionOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ]

  const onSubmit = (data: ReopenQueryFormData) => {
    onConfirm(data)
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: SPACING.borderRadiusLg,
          width: '600px',
          maxWidth: '90vw',
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ pt: SPACING.spacingXl, pb: SPACING.spacingMd }}>Re-open query?</DialogTitle>

        <DialogContent sx={{ px: SPACING.spacingXl, pb: SPACING.spacingXl }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: SPACING.spacingXl }}>
            {requiresTimepointResubmission
              ? 'Select the problematic container and specify a reason. The query and timepoint statuses will be updated.'
              : 'Specify a reason for reopening the query. Only the query status will be updated.'}
          </Typography>

          <TableContainer component={Paper} sx={{ mb: SPACING.spacingXl }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ backgroundColor: 'secondary.light' }}>
                  <TableCell sx={{ width: '40%', py: SPACING.spacingMd }}>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                      Timepoint:
                    </Typography>{' '}
                    {timepoint.name}
                  </TableCell>
                  <TableCell sx={{ width: '30%', py: SPACING.spacingMd }}>{capitalize(timepoint.status)}</TableCell>
                  <TableCell sx={{ width: '10%', textAlign: 'center', py: SPACING.spacingMd }}>
                    <ArrowForwardIcon color="action" />
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '20%',
                      py: SPACING.spacingMd,
                      color: requiresTimepointResubmission ? 'inherit' : 'text.disabled',
                    }}
                  >
                    {requiresTimepointResubmission ? 'Reopened' : 'Unchanged'}
                  </TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: 'secondary.light' }}>
                  <TableCell sx={{ py: SPACING.spacingMd }}>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                      Query:
                    </Typography>{' '}
                    {query.pseudo_id}
                  </TableCell>
                  <TableCell sx={{ py: SPACING.spacingMd }}>{formatQueryStatus(query.status)}</TableCell>
                  <TableCell sx={{ textAlign: 'center', py: SPACING.spacingMd }}>
                    <ArrowForwardIcon color="action" />
                  </TableCell>
                  <TableCell sx={{ py: SPACING.spacingMd }}>Reopened</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Stack spacing={SPACING.spacingLg}>
            <Stack direction="column" gap={SPACING.spacingSm}>
              <Typography>Should this query reopen the timepoint for submission?</Typography>
              <RadioGroup name="requires_timepoint_resubmission" options={resubmissionOptions} control={control} row />
            </Stack>

            {requiresTimepointResubmission && (
              <Dropdown
                name="container_ids"
                control={control}
                label="Select containers"
                options={containerOptions}
                multiple
                size="small"
              />
            )}

            <TextField
              name="reason"
              control={control}
              multiline
              rows={4}
              placeholder="Provide a reason"
              label="Reason"
              variant="outlined"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: SPACING.spacingXl, pb: SPACING.spacingXl, pt: 0 }}>
          <Button onClick={onClose} variant="contained" color="secondary" type="button">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={
              !reasonValue?.trim() ||
              (requiresTimepointResubmission && (!selectedContainers || selectedContainers.length === 0))
            }
          >
            Re-open query
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
